/** Неактивная вкладка ant не монтируется: обмен сессии запускает хозяин раздела, а не она сама */

import Vue from 'vue';

import { exchangeSession, isNoSpSessionError } from '../api/auth';
import {
  registerConfirmTokenHandler,
  registerReauthHandler,
  resetReauthState,
  readMmError,
} from '../api/mmHttp';
import { isMobileBackendConfigured } from '../config/env';
import { MmError } from './errors';
import {
  cancelScheduledRenewal,
  installTokenRenewalWatchers,
  MM_TOKEN_RENEW_LEAD_MS,
  MM_TOKEN_VISIBLE_RENEW_LEAD_MS,
  registerSilentRenewHandler,
  scheduleTokenRenewal,
  shouldRenewForLead,
} from './sessionRefresh';
import { hasSpAuthRecord } from './spSession';
import {
  clearToken,
  getRawAccessToken,
  getStoredToken,
  saveToken,
  setActiveTradingNetworkId,
} from './tokenStore';
import { MmErrorCode, MmSessionState } from '../types/enums';

interface MmSessionStore {
  state: MmSessionState;
  tradingNetworkId: number | null;
  error: MmError | null;
  /** Счётчик успешных обменов: компоненты следят за ним, чтобы перезагрузить данные */
  readyTick: number;
}

const store: MmSessionStore = Vue.observable({
  state: MmSessionState.IDLE,
  tradingNetworkId: null,
  error: null,
  readyTick: 0,
});

export function getSessionStore(): MmSessionStore {
  return store;
}

export function getSessionState(): MmSessionState {
  return store.state;
}

export function getSessionError(): MmError | null {
  return store.error;
}

export function getSessionTradingNetworkId(): number | null {
  return store.tradingNetworkId;
}

export function isSessionReady(): boolean {
  return store.state === MmSessionState.READY;
}

/** Результат обмена применяется, только если поколение не сменилось: иначе старый ответ затрёт токен */
let generation = 0;

/** Один промис обмена на все причины: иначе повторный обмен затрёт только что полученный токен. */
let inFlight: Promise<boolean> | null = null;

/** Токен с успешным ответом подтверждён: его 401 — истечение; у только что выданного 401 — отказ */
let confirmedBearer: string | null = null;
let issuedUnconfirmedBearer: string | null = null;

function resetTokenTrust(): void {
  confirmedBearer = null;
  issuedUnconfirmedBearer = null;
}

function noteTokenIssued(accessToken: string): void {
  issuedUnconfirmedBearer = accessToken;
  confirmedBearer = null;
}

function markTokenConfirmed(accessToken: string): void {
  confirmedBearer = accessToken;

  if (issuedUnconfirmedBearer === accessToken) {
    issuedUnconfirmedBearer = null;
  }
}

function isFreshUnconfirmedBearer(accessToken: string): boolean {
  return issuedUnconfirmedBearer === accessToken && confirmedBearer !== accessToken;
}

function canSilentReauthForCurrentBearer(): boolean {
  const current = getRawAccessToken();

  if (current === null) {
    return true;
  }

  return !isFreshUnconfirmedBearer(current);
}

function buildUnauthorizedError(): MmError {
  return readMmError({ response: { status: 401, data: null }, config: {} }, 'session-reauth');
}

function mapErrorToState(error: MmError): MmSessionState {
  if (error.code === MmErrorCode.MISCONFIGURED) {
    return MmSessionState.MISCONFIGURED;
  }

  if (error.code === MmErrorCode.FORBIDDEN || error.code === MmErrorCode.UNAUTHORIZED) {
    return MmSessionState.FORBIDDEN;
  }

  return MmSessionState.UNAVAILABLE;
}

/** Сбой упреждающего обмена не гасит текущий токен: за это отвечает keepCurrentOnFailure */
async function performExchange(
  tradingNetworkId: number,
  startedAt: number,
  keepCurrentOnFailure?: boolean,
): Promise<boolean> {
  try {
    const token = await exchangeSession(tradingNetworkId);

    if (startedAt !== generation) {
      // Контекст сменился за время запроса — результат устарел, не применяем
      return false;
    }

    saveToken(token);
    noteTokenIssued(token.accessToken);

    store.error = null;
    const wasReady = store.state === MmSessionState.READY;
    store.state = MmSessionState.READY;
    // Счётчик не трогаем при уже открытом разделе: иначе слетит несохранённое редактирование строк
    if (!wasReady) {
      store.readyTick += 1;
    }

    return true;
  } catch (error) {
    if (startedAt !== generation) {
      // Устаревший ответ не должен ни чистить токен, ни менять экран
      return false;
    }

    if (keepCurrentOnFailure === true && !isNoSpSessionError(error) && getRawAccessToken() !== null) {
      return false;
    }

    clearToken();
    resetTokenTrust();

    if (isNoSpSessionError(error)) {
      store.error = null;
      store.state = MmSessionState.SP_SESSION_EXPIRED;

      return false;
    }

    const mmError = readMmError(error, 'session-exchange');

    store.error = mmError;
    store.state = mapErrorToState(mmError);

    return false;
  }
}

export function ensureMobileSession(
  tradingNetworkId: number | null,
  force?: boolean,
  silent?: boolean,
  keepCurrentOnFailure?: boolean,
): Promise<boolean> {
  if (!isMobileBackendConfigured()) {
    store.state = MmSessionState.MISCONFIGURED;
    store.error = null;

    return Promise.resolve(false);
  }

  if (tradingNetworkId === null) {
    if (store.tradingNetworkId !== null) {
      return onTradingNetworkChanged(null);
    }

    store.state = MmSessionState.NETWORK_NOT_SELECTED;
    store.error = null;
    setActiveTradingNetworkId(null);

    return Promise.resolve(false);
  }

  if (!hasSpAuthRecord()) {
    store.state = MmSessionState.SP_SESSION_EXPIRED;
    store.error = null;

    return Promise.resolve(false);
  }

  /** Сеть меняем только через onTradingNetworkChanged, иначе старый обмен применит свой результат */
  if (store.tradingNetworkId !== tradingNetworkId) {
    return onTradingNetworkChanged(tradingNetworkId);
  }

  setActiveTradingNetworkId(tradingNetworkId);

  if (force !== true) {
    const existing = getStoredToken(tradingNetworkId);

    if (existing !== null) {
      scheduleTokenRenewal(existing.accessToken);

      if (shouldRenewForLead(existing.accessToken, MM_TOKEN_RENEW_LEAD_MS)) {
        return ensureMobileSession(tradingNetworkId, true, true, true);
      }

      if (store.state !== MmSessionState.READY) {
        store.state = MmSessionState.READY;
        store.error = null;
        store.readyTick += 1;
      }

      return Promise.resolve(true);
    }
  }

  if (inFlight !== null) {
    return inFlight;
  }

  if (silent !== true) {
    store.state = MmSessionState.PENDING;
  }

  store.error = null;

  const startedAt = generation;

  inFlight = performExchange(tradingNetworkId, startedAt, keepCurrentOnFailure).then((result) => {
    if (startedAt === generation) {
      inFlight = null;
    }

    return result;
  });

  return inFlight;
}

export function onModuleEnter(tradingNetworkId: number | null): void {
  safeRun(() => ensureMobileSession(tradingNetworkId));
}

/** Только первичная инициализация: данные перечитываются по «Применить», а не при выборе сети */
export function onMarketSelected(tradingNetworkId: number | null): void {
  if (store.tradingNetworkId !== null) {
    return;
  }

  safeRun(() => ensureMobileSession(tradingNetworkId));
}

export function onTradingNetworkChanged(tradingNetworkId: number | null): Promise<boolean> {
  const previous = store.tradingNetworkId;

  // Увеличение поколения гасит обмены прежней сети: их результат будет отброшен
  generation += 1;
  inFlight = null;
  resetReauthState();
  cancelScheduledRenewal();
  resetTokenTrust();

  const keepStored =
    (previous === null || previous === tradingNetworkId) &&
    tradingNetworkId !== null &&
    getStoredToken(tradingNetworkId) !== null;

  if (!keepStored) {
    clearToken();
  }

  store.error = null;
  store.tradingNetworkId = tradingNetworkId;
  setActiveTradingNetworkId(tradingNetworkId);
  store.state = tradingNetworkId === null ? MmSessionState.NETWORK_NOT_SELECTED : MmSessionState.IDLE;

  if (tradingNetworkId === null) {
    return Promise.resolve(false);
  }

  return ensureMobileSession(tradingNetworkId);
}

/** Замок снимает только повтор, запрошенный пользователем: иначе обмен зациклится */
export function retryMobileSession(userInitiated?: boolean): Promise<boolean> {
  const tradingNetworkId = store.tradingNetworkId;

  if (userInitiated !== true) {
    if (store.state === MmSessionState.FORBIDDEN || !canSilentReauthForCurrentBearer()) {
      return Promise.resolve(false);
    }

    if (tradingNetworkId === null) {
      return Promise.resolve(false);
    }

    return ensureMobileSession(tradingNetworkId, true, true);
  }

  generation += 1;
  inFlight = null;
  resetReauthState();
  cancelScheduledRenewal();
  resetTokenTrust();

  store.error = null;

  if (tradingNetworkId === null) {
    store.state = MmSessionState.NETWORK_NOT_SELECTED;

    return Promise.resolve(false);
  }

  store.state = MmSessionState.IDLE;

  return ensureMobileSession(tradingNetworkId, true);
}

function safeRun(action: () => Promise<boolean>): void {
  try {
    const result = action();

    if (result && typeof result.catch === 'function') {
      result.catch(() => undefined);
    }
  } catch (e) {
    // Недоступность мобильного контура не должна ломать остальной модуль «Мониторинг цен»
  }
}

function requestSilentRenew(leadMs: number): void {
  if (store.state === MmSessionState.FORBIDDEN) {
    return;
  }

  const tradingNetworkId = store.tradingNetworkId;

  if (tradingNetworkId === null) {
    return;
  }

  if (!hasSpAuthRecord()) {
    return;
  }

  const token = getRawAccessToken();

  if (token === null) {
    return;
  }

  if (!shouldRenewForLead(token, leadMs)) {
    scheduleTokenRenewal(token);

    return;
  }

  safeRun(() => ensureMobileSession(tradingNetworkId, true, true, true));
}

/** Повторный обмен: один на запрос и один общий на все параллельные запросы */
registerReauthHandler(() => {
  const tradingNetworkId = store.tradingNetworkId;

  if (tradingNetworkId === null) {
    return Promise.resolve(false);
  }

  if (inFlight !== null) {
    // Обмен уже идёт — ждём его, второй запуск снёс бы результат первого
    return inFlight;
  }

  const current = getRawAccessToken();

  if (current !== null && isFreshUnconfirmedBearer(current)) {
    cancelScheduledRenewal();
    store.error = buildUnauthorizedError();
    store.state = MmSessionState.FORBIDDEN;

    return Promise.resolve(false);
  }

  // `silent = true`: фоновый обмен не показывает заглушку загрузки, отказ по-прежнему блокирует
  return ensureMobileSession(tradingNetworkId, true, true);
});

registerConfirmTokenHandler((tokenUsed) => {
  if (!tokenUsed) {
    return;
  }

  const current = getRawAccessToken();

  if (current !== tokenUsed) {
    return;
  }

  markTokenConfirmed(tokenUsed);
});

registerSilentRenewHandler(() => {
  requestSilentRenew(MM_TOKEN_VISIBLE_RENEW_LEAD_MS);
});

installTokenRenewalWatchers();
